import React, { useEffect } from "react";
import {
  Button,
  Checkbox,
  Form,
  Input,
  FormInstance,
  Space,
  Spin,
  Tooltip,
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { gql, useQuery } from "@apollo/client";

type PermissionOption = { id: string; description: string };

const AVAILABLE_PERMISSIONS_QUERY = gql`
  query AvailablePermissions {
    availablePermissions {
      id
      description
    }
  }
`;

type UserFormProps = {
  form: FormInstance;
  saving: boolean;
  emailLocked?: boolean;
  submitLabel: string;
  onCancel: () => void;
};

const UserForm: React.FC<UserFormProps> = ({
  form,
  saving,
  emailLocked = false,
  submitLabel,
  onCancel,
}) => {
  const watchedPermissions: string[] =
    Form.useWatch("permissionIds", form) ?? [];

  const { data: permissionsData, loading: permissionsLoading } = useQuery(
    AVAILABLE_PERMISSIONS_QUERY,
  );
  const availablePermissions: PermissionOption[] =
    permissionsData?.availablePermissions ?? [];

  useEffect(() => {
    if (
      watchedPermissions.includes("migrations_runs.manage") &&
      !watchedPermissions.includes("users.read")
    ) {
      form.setFieldValue("permissionIds", [
        ...watchedPermissions,
        "users.read",
      ]);
    }
  }, [watchedPermissions, form]);

  return (
    <>
      <Form.Item
        label="First Name"
        name="firstName"
        rules={[{ required: true, message: "Required" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Last Name"
        name="lastName"
        rules={[{ required: true, message: "Required" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        normalize={(value?: string) => value?.trim()}
        rules={
          emailLocked
            ? []
            : [
                { required: true, message: "Required" },
                { type: "email", message: "Must be a valid email" },
              ]
        }
        extra={
          emailLocked
            ? "To change email, create a new user and mark this one inactive."
            : undefined
        }
      >
        <Input disabled={emailLocked} />
      </Form.Item>

      <Form.Item label="Calendar Link" name="calendarLink">
        <Input placeholder="https://calendly.com/..." />
      </Form.Item>

      <Form.Item name="active" valuePropName="checked">
        <Checkbox>Active</Checkbox>
      </Form.Item>

      <Form.Item label="Permissions" name="permissionIds">
        {permissionsLoading ? (
          <Spin size="small" />
        ) : (
          <Checkbox.Group
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {availablePermissions.map((p) => {
              const isViewUsers = p.id === "users.read";
              const forcedOn =
                isViewUsers &&
                watchedPermissions.includes("migrations_runs.manage");
              const label = isViewUsers ? (
                <>
                  {p.description}
                  <Tooltip title="Required to create migration runs — enables picking a migration rep from the user list">
                    <QuestionCircleOutlined
                      style={{ marginLeft: 6, color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </>
              ) : (
                p.description
              );
              return (
                <Checkbox key={p.id} value={p.id} disabled={forcedOn}>
                  {label}
                </Checkbox>
              );
            })}
          </Checkbox.Group>
        )}
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={saving}>
            {submitLabel}
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </Space>
      </Form.Item>
    </>
  );
};

export default UserForm;
